import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { BarChartPro } from "@mui/x-charts-pro/BarChartPro";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RangeEstimate, useRangeEstimate } from "@/hooks/rangeEstimate/useRangeEstimate";
import i18n from "@/i18n";

// ─── Types ────────────────────────────────────────────────────────────────────

// A type alias (not an interface) so it satisfies the chart's indexed dataset type.
type MonthlyDataPoint = {
  month: string;
  monthFull: string;
  mean: number;
  lower: number;
  upper: number;
};

type SeriesKey = "lower" | "mean" | "upper";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function aggregateByMonth(sorted: RangeEstimate[]): MonthlyDataPoint[] {
  const buckets = new Map<string, { sum: [number, number, number]; count: number; date: Date }>();

  for (const r of sorted) {
    const d = new Date(r.detectionDate);
    const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, "0")}`;
    const entry = buckets.get(key);
    if (entry) {
      entry.sum[0] += r.rangeEstimateMeanKm;
      entry.sum[1] += r.rangeEstimateLowerBoundKm;
      entry.sum[2] += r.rangeEstimateUpperBoundKm;
      entry.count++;
    } else {
      buckets.set(key, {
        sum: [r.rangeEstimateMeanKm, r.rangeEstimateLowerBoundKm, r.rangeEstimateUpperBoundKm],
        count: 1,
        date: d,
      });
    }
  }

  const fmt = new Intl.DateTimeFormat(i18n.language, { month: "short" });
  const fmtFull = new Intl.DateTimeFormat(i18n.language, { month: "short", year: "numeric" });

  return [...buckets.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, { sum, count, date }]) => ({
      month: fmt.format(date),
      monthFull: fmtFull.format(date),
      mean: Math.round(sum[0] / count),
      lower: Math.round(sum[1] / count),
      upper: Math.round(sum[2] / count),
    }));
}

function formatMethod(method: string): string {
  if (method === "BatteryEstimation") return "Battery";
  if (method === "SoCEstimation") return "SoC";
  return method;
}

// ─── Tooltip ─────────────────────────────────────────────────────────────────

interface AxisContentProps {
  axisValue?: string | number | Date | null;
}

function makeAxisContent(
  dataset: MonthlyDataPoint[],
  lowerLabel: string,
  meanLabel: string,
  upperLabel: string,
) {
  return function AxisContent({ axisValue }: AxisContentProps) {
    const point = dataset.find(d => d.month === String(axisValue ?? ""));
    if (!point) return null;
    return (
      <Box sx={{ p: 1.5, minWidth: 160, bgcolor: "background.paper" }}>
        <Typography variant="caption" fontWeight={700} display="block" mb={0.75}>
          {point.monthFull}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
          <TooltipRow color="#42a5f5" label={upperLabel} value={point.upper} />
          <TooltipRow color="#1976d2" label={meanLabel} value={point.mean} />
          <TooltipRow color="#90caf9" label={lowerLabel} value={point.lower} />
        </Box>
      </Box>
    );
  };
}

function TooltipRow({ color, label, value }: { color: string; label: string; value: number }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: color, flexShrink: 0 }} />
      <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
        {label}
      </Typography>
      <Typography variant="caption" fontWeight={600}>
        {value} {" km"}
      </Typography>
    </Box>
  );
}

// ─── Chart ───────────────────────────────────────────────────────────────────

const RangeChart: React.FC<{
  dataset: MonthlyDataPoint[];
  labels: { lower: string; mean: string; upper: string };
}> = ({ dataset, labels }) => {
  const yFmt = (v: number | null) => (v !== null ? `${v} km` : "");
  const [activeSeries, setActiveSeries] = useState<SeriesKey | null>(null);

  const AxisContent = useMemo(
    () => makeAxisContent(dataset, labels.lower, labels.mean, labels.upper),
    [dataset, labels.lower, labels.mean, labels.upper],
  );

  const highlightedItem = activeSeries ? { seriesId: activeSeries } : null;

  return (
    <BarChartPro
      skipAnimation
      dataset={dataset}
      xAxis={[
        {
          scaleType: "band" as const,
          dataKey: "month",
          tickLabelStyle: { fontSize: 11 },
          valueFormatter: (value: string, ctx: { location: string }) =>
            ctx.location !== "tick"
              ? (dataset.find(d => d.month === value)?.monthFull ?? value)
              : value,
        },
      ]}
      series={[
        {
          id: "lower",
          dataKey: "lower",
          label: labels.lower,
          color: "#90caf9",
          valueFormatter: yFmt,
          highlightScope: { highlight: "series", fade: "global" },
        },
        {
          id: "mean",
          dataKey: "mean",
          label: labels.mean,
          color: "#1976d2",
          valueFormatter: yFmt,
          highlightScope: { highlight: "series", fade: "global" },
        },
        {
          id: "upper",
          dataKey: "upper",
          label: labels.upper,
          color: "#42a5f5",
          valueFormatter: yFmt,
          highlightScope: { highlight: "series", fade: "global" },
        },
      ]}
      yAxis={[{ valueFormatter: yFmt }]}
      axisHighlight={{ x: "band" }}
      tooltip={{ trigger: "axis", axisContent: AxisContent }}
      borderRadius={3}
      {...({
        highlightedItem,
        onHighlightChange: () => {
          /* suppress internal hover-driven highlight changes */
        },
      } as object)}
      slotProps={{
        legend: {
          position: { vertical: "top", horizontal: "middle" },
          itemGap: 16,
          labelStyle: { fontSize: 12 },
          itemMarkHeight: 12,
          itemMarkWidth: 12,
          onItemClick: (
            _event: React.MouseEvent<SVGRectElement, MouseEvent>,
            legendItem: { seriesId: number | string },
          ) => {
            const id = legendItem.seriesId;
            if (id !== "lower" && id !== "mean" && id !== "upper") return;
            setActiveSeries(prev => (prev === id ? null : id));
          },
        },
      }}
      margin={{ left: 70, right: 10, top: 50, bottom: 30 }}
    />
  );
};

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, primary }: { label: string; value: string; primary?: boolean }) {
  return (
    <Box
      sx={{
        flex: "1 1 120px",
        minWidth: 120,
        p: 2,
        borderRadius: 2,
        bgcolor: primary ? "primary.main" : "grey.100",
        color: primary ? "primary.contrastText" : "text.primary",
      }}
    >
      <Typography variant="caption" sx={{ opacity: 0.8 }}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={600}>
        {value}
      </Typography>
    </Box>
  );
}

// ─── Main export ─────────────────────────────────────────────────────────────

const RangeEstimateChart: React.FC<{ deviceId: string }> = ({ deviceId }) => {
  const { t } = useTranslation();
  const { data: rangeEstimates = [], isLoading } = useRangeEstimate(deviceId);

  const sorted = [...rangeEstimates].sort(
    (a, b) => new Date(a.detectionDate).getTime() - new Date(b.detectionDate).getTime(),
  );

  const latestEstimate = sorted.at(-1);
  const dataset = aggregateByMonth(sorted);

  const labels = {
    lower: t("modal.rangeEstimate.lowerBound"),
    mean: t("modal.rangeEstimate.mean"),
    upper: t("modal.rangeEstimate.upperBound"),
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (sorted.length === 0) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <Typography color="text.secondary">{t("modal.notEnoughData")}</Typography>
      </Box>
    );
  }

  return (
    <>
      {latestEstimate && (
        <Box sx={{ display: "flex", gap: 3, mb: 3, flexWrap: "wrap" }}>
          <StatCard
            label={labels.mean}
            value={`${Math.round(latestEstimate.rangeEstimateMeanKm)} km`}
            primary
          />
          <StatCard
            label={labels.lower}
            value={`${Math.round(latestEstimate.rangeEstimateLowerBoundKm)} km`}
          />
          <StatCard
            label={labels.upper}
            value={`${Math.round(latestEstimate.rangeEstimateUpperBoundKm)} km`}
          />
          <StatCard
            label={t("modal.rangeEstimate.method")}
            value={formatMethod(latestEstimate.rangeEstimationMethod)}
          />
        </Box>
      )}

      <Typography variant="subtitle2" color="text.secondary" mb={1}>
        {t("modal.rangeEstimate.chartTitle")}
      </Typography>

      <Box sx={{ width: "100%", height: 320 }}>
        <RangeChart dataset={dataset} labels={labels} />
      </Box>
    </>
  );
};

export default RangeEstimateChart;
